import axios from 'axios';
import {
  getToken,
  setToken,
  clearToken,
  isTokenNearExpiry,
} from '@crema/services/auth/jwt-auth/token';

let authFailureHandler = null;

export const registerAuthFailureHandler = (callback) => {
  authFailureHandler = typeof callback === 'function' ? callback : null;
};

const notifyAuthFailure = async (error) => {
  if (authFailureHandler) {
    try {
      await authFailureHandler(error);
    } catch (callbackError) {
      console.error('Auth failure handler threw an error', callbackError);
    }
  }
};

// Configurable por .env si cambia la ruta:
const REFRESH_PATH =
  process.env.REACT_APP_REFRESH_PATH || '/api/Usuarios/refresh';

// Evita múltiples refresh concurrentes
let refreshPromise = null;

// Esta función usa la API base (no arca) para refrescar el token.
// Si tu endpoint de refresh es otro, cambiá REFRESH_PATH arriba.
async function refreshAccessToken(apiBaseInstance) {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const current = getToken();
      if (!current) throw new Error('No hay token para refrescar');

      // Algunas APIs de refresh aceptan el token actual en el Authorization.
      // Otras esperan body { token }. Ajustá según tu backend.
      const res = await apiBaseInstance.post(
        REFRESH_PATH,
        {}, // ó { token: current }
        { headers: { Authorization: `Bearer ${current}` } },
      );

      // Suponemos que devuelve { token, expiresAt? }
      const newToken = res?.data?.token ?? res?.data;
      const newExpiresAt = res?.data?.expiresAt; // opcional
      if (!newToken) throw new Error('Refresh no devolvió token');

      setToken(newToken, newExpiresAt);
      return newToken;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Adjunta interceptores de auth/refresh a una instancia de axios.
 * - apiBaseInstance: debe ser TU API principal (la que tiene /Usuarios/refresh)
 * - instance: puede ser tu api base o arcaApi (ambas se benefician del mismo flujo)
 */
export function attachAuthInterceptors(instance, apiBaseInstance) {
  // REQUEST: siempre leer el token más reciente (no lo congeles en headers al crear la instancia)
  instance.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // RESPONSE: si 401 por token vencido => refrescar una vez y reintentar
  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const originalRequest = error.config;

      // Solo intentamos refresh/retry si no lo hicimos ya
      const status = error?.response?.status;
      const wwwAuth = error?.response?.headers?.['www-authenticate'] || '';
      const isExpired =
        status === 401 &&
        (wwwAuth.includes('invalid_token') ||
          wwwAuth.includes('expired') ||
          error?.response?.data
            ?.toString?.()
            .toLowerCase?.()
            .includes('expired'));

      // Pre-refresh preventivo (opcional) si tenés expiresAt:
      // if (!originalRequest._retry && isTokenNearExpiry()) { ... }

      if (isExpired && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const newToken = await refreshAccessToken(apiBaseInstance);
          // Reinyectar header y reintentar
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        } catch (e) {
          // Refresh falló: limpiar y propagar 401
          clearToken();
          await notifyAuthFailure(error);
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    },
  );
}
